import { AsyncStorage } from '@utils/storage';
import { isUrlExpired } from '@utils/urlExpiry';

export interface ResolvableDocument {
  clientId: string;
  file?: { url?: string | null } | null;
}

export type FetchDocumentUrl = (
  clientId: string
) => Promise<string | null | undefined>;

/**
 * Resolves a usable, non-expired URL for a document, actively fetching a
 * fresh one via `fetchUrlByClientId` when neither the document payload nor
 * the AsyncStorage cache has one. This removes the dependency on background
 * cache pre-warming that previously made file-preview/share fail silently
 * (TA-10376).
 */
export async function resolveDocumentUrl(
  doc: ResolvableDocument,
  fetchUrlByClientId: FetchDocumentUrl
): Promise<string | null> {
  const now = new Date();

  if (doc.file?.url && !isUrlExpired(doc.file.url, now)) {
    return doc.file.url;
  }

  const cachedUrl = await AsyncStorage.getItem(doc.clientId);
  if (cachedUrl && !isUrlExpired(cachedUrl, now)) {
    return cachedUrl;
  }

  const fetchedUrl = await fetchUrlByClientId(doc.clientId);
  if (fetchedUrl && !isUrlExpired(fetchedUrl, now)) {
    await AsyncStorage.setItem(doc.clientId, fetchedUrl);
    return fetchedUrl;
  }

  return null;
}
